// Resume Pagination System
// Implements measurement-then-placement algorithm with intelligent content splitting

const PAGE_HEIGHT_IN = 11;
const PAGE_WIDTH_IN = 8.5;
const DPI = 96;

export interface PageMargins {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PaginationConfig {
  margins: PageMargins;
  pageHeight?: number;
  pageWidth?: number;
  dpi?: number;
}

export class ResumePaginator {
  private margins: PageMargins;
  private pageHeight: number;
  private pageWidth: number;
  private dpi: number;
  private currentPage: HTMLElement | null = null;
  private currentHeight: number = 0;
  private maxHeight: number;
  private pageNumber: number = 0;

  constructor(config: PaginationConfig) {
    this.margins = config.margins;
    this.pageHeight = config.pageHeight || PAGE_HEIGHT_IN;
    this.pageWidth = config.pageWidth || PAGE_WIDTH_IN;
    this.dpi = config.dpi || DPI;
    this.maxHeight = this.getAvailableHeight();
  }

  /**
   * Calculate available height per page in pixels
   */
  private getAvailableHeight(): number {
    return (this.pageHeight - this.margins.top - this.margins.bottom) * this.dpi;
  }

  /**
   * Calculate available width per page in pixels
   */
  private getAvailableWidth(): number {
    return (this.pageWidth - this.margins.left - this.margins.right) * this.dpi;
  }

  /**
   * Measure element height using shadow DOM technique
   */
  measureElementHeight(element: HTMLElement): number {
    // Use a hidden wrapper that mirrors the page's typography and width
    const wrapper = document.createElement('div');
    wrapper.className = 'resume-page measurement';
    wrapper.style.position = 'absolute';
    wrapper.style.visibility = 'hidden';
    wrapper.style.left = '-10000px';
    wrapper.style.top = '0';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.boxSizing = 'content-box';
    wrapper.style.width = `${this.getAvailableWidth()}px`;

    // Typography parity with createPageElement
    wrapper.style.fontFamily = 'Cambria, Georgia, serif';
    wrapper.style.fontSize = '10pt';
    wrapper.style.lineHeight = '1.2';
    wrapper.style.color = '#111827';

    const clone = element.cloneNode(true) as HTMLElement;
    wrapper.appendChild(clone);

    document.body.appendChild(wrapper);
    const height = wrapper.scrollHeight;
    document.body.removeChild(wrapper);

    return height;
  }

  /**
   * Create a new page element
   */
  private createPageElement(pageNum: number): HTMLElement {
    const page = document.createElement('div');
    page.className = 'resume-page';
    page.setAttribute('data-page', pageNum.toString());
    
    // Calculate content area dimensions (excluding padding)
    const contentHeight = this.pageHeight - this.margins.top - this.margins.bottom;
    const contentWidth = this.pageWidth - this.margins.left - this.margins.right;
    
    page.style.height = `${contentHeight}in`;
    page.style.width = `${contentWidth}in`;
    page.style.maxWidth = `${contentWidth}in`;
    page.style.padding = `${this.margins.top}in ${this.margins.right}in ${this.margins.bottom}in ${this.margins.left}in`;
    page.style.backgroundColor = 'white';
    page.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
    page.style.marginBottom = '1.5rem';
    page.style.border = '1px solid #e5e7eb';
    page.style.borderRadius = '2px';
    page.style.boxSizing = 'content-box'; // CRITICAL: padding is outside content area
    page.style.fontFamily = 'Cambria, Georgia, serif';
    page.style.fontSize = '10pt';
    page.style.lineHeight = '1.2';
    page.style.color = '#111827';
    page.style.overflow = 'hidden';
    
    return page;
  }

  /**
   * Split content that exceeds page height
   */
  private splitContent(element: HTMLElement, remainingHeight: number): [HTMLElement, HTMLElement] {
    // Clone element to safely inspect children and compute heights
    const clone = element.cloneNode(true) as HTMLElement;
    const children = Array.from(clone.children) as HTMLElement[];

    const firstPart = element.cloneNode(false) as HTMLElement;
    const secondPart = element.cloneNode(false) as HTMLElement;

    let accumulatedHeight = 0;

    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      const childHeight = this.measureElementHeight(child);

      // If whole child fits, keep it in the top part
      if (accumulatedHeight + childHeight <= remainingHeight) {
        firstPart.appendChild(child.cloneNode(true));
        accumulatedHeight += childHeight;
        continue;
      }

      // Child doesn't fit -> try semantic split for lists (UL/OL) at LI boundaries
      const tag = child.tagName.toUpperCase();
      if (tag === 'UL' || tag === 'OL') {
        const listTop = child.cloneNode(false) as HTMLElement;
        const listBottom = child.cloneNode(false) as HTMLElement;
        const items = Array.from(child.children) as HTMLElement[];

        let listAccum = 0;
        for (let k = 0; k < items.length; k++) {
          const li = items[k] as HTMLElement;
          const liHeight = this.measureElementHeight(li);

          // Only add to top while it fits; once bottom starts, remaining items go to bottom
          if (accumulatedHeight + listAccum + liHeight <= remainingHeight && listBottom.children.length === 0) {
            listTop.appendChild(li.cloneNode(true));
            listAccum += liHeight;
          } else {
            listBottom.appendChild(li.cloneNode(true));
          }
        }

        // WIDOW/ORPHAN CONTROL: If listTop is empty and firstPart has header siblings,
        // move those headers to secondPart to prevent orphaned job headings
        if (listTop.children.length === 0 && firstPart.children.length > 0) {
          // Check if any preceding siblings should stick with the list
          const precedingSiblings = Array.from(firstPart.children) as HTMLElement[];
          let shouldMoveHeaders = false;
          
          for (const sibling of precedingSiblings) {
            if (sibling.getAttribute('data-stick-with-next-list') === 'true') {
              shouldMoveHeaders = true;
              break;
            }
          }
          
          // Move all preceding siblings to secondPart to avoid orphans
          if (shouldMoveHeaders) {
            while (firstPart.firstChild) {
              secondPart.insertBefore(firstPart.firstChild, secondPart.firstChild);
            }
          }
        }

        if (listTop.children.length > 0) firstPart.appendChild(listTop);
        if (listBottom.children.length > 0) secondPart.appendChild(listBottom);

        // Move remaining siblings after this child to bottom part
        for (let j = i + 1; j < children.length; j++) {
          secondPart.appendChild(children[j].cloneNode(true));
        }

        return [firstPart, secondPart];
      }

      // Non-list element -> move it (and the rest) to bottom part
      secondPart.appendChild(child.cloneNode(true));
      for (let j = i + 1; j < children.length; j++) {
        secondPart.appendChild(children[j].cloneNode(true));
      }

      return [firstPart, secondPart];
    }

    // If we never overflowed, bottom stays empty
    return [firstPart, secondPart];
  }

  /**
   * Main pagination function
   */
  paginate(container: HTMLElement, sections: HTMLElement[]): HTMLElement {
    const paginatedContainer = document.createElement('div');
    paginatedContainer.className = 'resume-paginated-container';
    
    // Inject CSS to remove bottom margins from last elements on each page
    const style = document.createElement('style');
    style.textContent = `
      .resume-page > *:last-child { margin-bottom: 0 !important; }
      .resume-page > *:last-child :is(p, ul, ol, h1, h2, h3, h4, h5, h6, div, section):last-child { margin-bottom: 0 !important; }
      .resume-page :is(p, ul, ol):last-child { margin-bottom: 0 !important; }
    `;
    paginatedContainer.appendChild(style);
    
    // Initialize first page
    this.pageNumber = 1;
    this.currentPage = this.createPageElement(this.pageNumber);
    this.currentHeight = 0;
    
    paginatedContainer.appendChild(this.currentPage);
    
    // Process each section
    for (const section of sections) {
      this.processSection(section, paginatedContainer);
    }
    
    return paginatedContainer;
  }

  /**
   * Process individual section following EXACT state machine decision tree
   */
  private processSection(section: HTMLElement, container: HTMLElement) {
    const keepTogether = section.getAttribute('data-keep-together') === 'true';
    const isContinuation = section.getAttribute('data-experience-continuation') === 'true';
    const height = this.measureElementHeight(section);
    const availableHeight = this.maxHeight - this.currentHeight;

    // Debug: measurement parity + decision context
    console.debug('[Paginator] Section decision', {
      pageNumber: this.pageNumber,
      tag: section.tagName,
      height,
      availableHeight,
      keepTogether,
      isContinuation,
    });

    // STATE MACHINE - EXACT DECISION CASCADE
    
    // 1. Does it fit on current page?
    if (height <= availableHeight) {
      this.currentPage!.appendChild(section.cloneNode(true));
      this.currentHeight += height;
      return;
    }

    // 2. Doesn't fit. Is it marked keep-together OR experience-continuation (both atomic)?
    if (keepTogether || isContinuation) {
      // 2a. Start new page
      this.pageNumber++;
      this.currentPage = this.createPageElement(this.pageNumber);
      this.currentHeight = 0;
      container.appendChild(this.currentPage);
      
      // 2b. Does it fit on fresh page?
      if (height <= this.maxHeight) {
        this.currentPage!.appendChild(section.cloneNode(true));
        this.currentHeight += height;
        return;
      }
      
      // 2c. Too huge even for fresh page - must split recursively
      this.splitAndPaginate(section, container);
      return;
    }

    // 3. Not atomic, doesn't fit - can split
    const [top, bottom] = this.splitContent(section, availableHeight);
    const madeTopProgress = !!(top && top.textContent?.trim());
    
    if (madeTopProgress) {
      this.currentPage!.appendChild(top);
      this.currentHeight = this.maxHeight; // page filled
    }
    
    if (bottom && bottom.textContent?.trim()) {
      const bottomHeight = this.measureElementHeight(bottom);
      
      // Always move to a fresh page for the bottom part
      this.pageNumber++;
      this.currentPage = this.createPageElement(this.pageNumber);
      this.currentHeight = 0;
      container.appendChild(this.currentPage);
      
      // RECURSION GUARD: if no progress and bottom still can't fit on a fresh page,
      // force iterative splitting to guarantee forward progress
      if (!madeTopProgress && bottomHeight > this.maxHeight) {
        this.splitAndPaginate(bottom, container);
        return;
      }
      
      // Normal case: recurse for the bottom part
      this.processSection(bottom, container);
    }
  }

  /**
   * Split huge content recursively using WHILE loop (as specified)
   */
  private splitAndPaginate(section: HTMLElement, container: HTMLElement) {
    let remaining = section;
    let iterations = 0;
    let prevSig = '';
    
    while (remaining && remaining.textContent?.trim()) {
      iterations++;
      const availableHeight = this.maxHeight - this.currentHeight;
      const remainingHeight = this.measureElementHeight(remaining);
      
      // Fits now, place and finish
      if (remainingHeight <= availableHeight) {
        this.currentPage!.appendChild(remaining.cloneNode(true));
        this.currentHeight += remainingHeight;
        break;
      }
      
      // Attempt split
      const [top, bottom] = this.splitContent(remaining, availableHeight);
      const madeTop = !!(top && top.textContent?.trim());
      
      if (madeTop) {
        this.currentPage!.appendChild(top);
        this.currentHeight = this.maxHeight;
      }
      
      // Move to next page
      this.pageNumber++;
      this.currentPage = this.createPageElement(this.pageNumber);
      this.currentHeight = 0;
      container.appendChild(this.currentPage);
      
      // NO-PROGRESS GUARD: if top is empty and remaining isn't changing across iterations
      const sig = `${remaining.innerHTML.length}:${remaining.children.length}`;
      if (!madeTop && bottom && sig === prevSig) {
        console.warn('[Paginator] No-progress split detected. Placing remaining to avoid infinite loop.');
        this.currentPage!.appendChild(remaining.cloneNode(true));
        this.currentHeight = this.maxHeight;
        break;
      }
      prevSig = sig;
      
      // ITERATION CAP GUARD
      if (iterations > 200) {
        console.warn('[Paginator] Iteration cap reached. Placing remaining to avoid hang.');
        this.currentPage!.appendChild(remaining.cloneNode(true));
        this.currentHeight = this.maxHeight;
        break;
      }
      
      remaining = bottom;
    }
  }
}

/**
 * Helper function to prepare resume sections for pagination
 */
export function prepareSectionsForPagination(resumeElement: HTMLElement): HTMLElement[] {
  const sections: HTMLElement[] = [];
  const children = Array.from(resumeElement.children);
  
  children.forEach((child) => {
    const section = child as HTMLElement;
    // Do not auto-apply any attributes; templates control atomicity explicitly
    sections.push(section);
  });
  
  return sections;
}
